import { addDays, addHours, differenceInDays, endOfDay, set, startOfDay } from "date-fns";
import { parseDate } from "../../../lib/dateUtils";
import { CHARGE_STATUS, RERSERVATION_STATUS } from "../../witadmin/Common/constants";
import {
  AutomaticChargeEntity,
  AutomaticChargesDTO,
  CancellationPolicyConfiguration,
  CancellationPolicyConfigurationFromRoomStay,
  CancellationPolicyConfigurationType,
  CancellationPolicyEntity,
  PenaltyBracket,
  ReleaseConfiguration,
} from "./automaticCharges.dto";

function calculatePenalty(
  firstNightCost: number,
  totalRate: number,
  configuredPenalty: number,
  type: CancellationPolicyConfigurationType,
  currency: string
): number {
  switch (type) {
    case "FIX_AMOUNT":
      return configuredPenalty;
    case "PERCENTAGE":
      return totalRate * (configuredPenalty / 100);
    case "CHARGE_FIRST_NIGHT":
      return firstNightCost;
    default: {
      throw new Error(`Invalid value provided ${JSON.stringify(type)}`);
    }
  }
}

function calculatePolicyDate(
  checkIn: Date,
  checkOut: Date,
  releaseConfiguration: ReleaseConfiguration | undefined,
  isEnd: boolean,
  cutOffDate: Date = new Date()
): Date {
  const duration = releaseConfiguration?.duration ?? 0;
  const time = releaseConfiguration?.hour;
  const chronoUnit = releaseConfiguration?.chronoUnit;
  // when no release configuration is defined it means the configuration is open-ended in that side
  // if it's the start side (maxRelease) then the policy starts at the beginning of the cutoff date
  // if it's the end side (minRelease) then the policy starts ends at the end of the checkout date
  const isDurationValid =
    duration !== undefined && !Number.isNaN(duration) && Number.isFinite(duration);
  let date: Date | undefined;
  if (isDurationValid && chronoUnit) {
    date = chronoUnit === "DAYS" ? addDays(checkIn, -duration) : addHours(checkIn, -duration);
  } else {
    date = isEnd ? checkOut : cutOffDate;
  }
  // since we always get a default time of 23:59, we ignore it if there's no valid duration accompanying it
  if (time && isDurationValid) {
    const [hoursStr, minutesStr] = time.split(":");
    return set(date, {
      hours: parseInt(hoursStr),
      minutes: parseInt(minutesStr),
      seconds: 0,
      milliseconds: 0,
    });
  }
  return isEnd ? endOfDay(date) : startOfDay(date);
}

function buildPenaltyBrackets({
  configurations,
  checkIn,
  checkOut,
  cutOffDate,
  currency,
  firstNightCost,
  totalRate,
}: {
  configurations: CancellationPolicyConfigurationFromRoomStay[];
  checkIn: Date;
  checkOut: Date;
  cutOffDate: Date;
  currency: string;
  firstNightCost: number;
  totalRate: number;
}): PenaltyBracket[] {
  if (configurations.length === 1) {
    return configurations.map(it => {
      const isEndDurationValid =
        it.minRelease?.duration !== undefined &&
        !Number.isNaN(it.minRelease?.duration) &&
        Number.isFinite(it.minRelease?.duration);
      const startDate = calculatePolicyDate(checkIn, checkOut, it.maxRelease, false, cutOffDate);
      let endDate = isEndDurationValid
        ? calculatePolicyDate(checkIn, checkOut, it.minRelease, true, cutOffDate)
        : undefined;
      if (endDate && endDate.getTime() < startDate.getTime()) {
        endDate = endOfDay(startDate);
      }
      return {
        startDate,
        startHour: it.maxRelease?.hour === "23:59" ? undefined : it.maxRelease?.hour,
        endDate: endDate,
        endHour: it.minRelease?.hour === "23:59" ? undefined : it.minRelease?.hour,
        penalty: calculatePenalty(firstNightCost, totalRate, it.penalty, it.type, currency),
        type: it.type,
        penaltyBase: it.penalty,
        canCancel: it.cancelOnline,
      };
    });
  }

  const penaltyBrackets = configurations.map(it => {
    const isStartDurationValid =
      it.maxRelease?.duration !== undefined &&
      !Number.isNaN(it.maxRelease?.duration) &&
      Number.isFinite(it.maxRelease?.duration);
    const isEndDurationValid =
      it.minRelease?.duration !== undefined &&
      !Number.isNaN(it.minRelease?.duration) &&
      Number.isFinite(it.minRelease?.duration);
    return {
      startDate: isStartDurationValid
        ? calculatePolicyDate(checkIn, checkOut, it.maxRelease, false, cutOffDate)
        : undefined,
      startHour: it.maxRelease?.hour === "23:59" ? undefined : it.maxRelease?.hour,
      endDate: isEndDurationValid
        ? calculatePolicyDate(checkIn, checkOut, it.minRelease, true, cutOffDate)
        : undefined,
      endHour: it.minRelease?.hour === "23:59" ? undefined : it.minRelease?.hour,
      penalty: calculatePenalty(firstNightCost, totalRate, it.penalty, it.type, currency),
      type: it.type,
      penaltyBase: it.penalty,
      canCancel: it.cancelOnline,
    };
  });

  return [...penaltyBrackets].sort((a, b) => {
    if (a.startDate === undefined) {
      return -1;
    }
    if (b.startDate === undefined) {
      return 1;
    }
    return a.startDate.getTime() - b.startDate.getTime();
  });
}

function formatConfigurations(configurations: CancellationPolicyConfiguration[]) {
  return (
    configurations.map(it => {
      const minReleaseDate = it.minRelease ? parseDate(it.minRelease, "yyyy-MM-dd") : undefined;
      const maxReleaseDate = it.maxRelease ? parseDate(it.maxRelease, "yyyy-MM-dd") : undefined;
      return {
        type: it.type,
        cancelOnline: it.cancelOnline,
        penalty: it.penalty ?? 0,
        minRelease: {
          hour: it.minReleaseTimeLimit,
          timezone: it.minReleaseTimeZone,
        },
        maxRelease: {
          hour: it.maxReleaseTimeLimit,
          timezone: it.maxReleaseTimeZone,
        },
        minReleaseDate,
        maxReleaseDate,
      };
    }) ?? []
  );
}

function getReservationStatus(status: string) {
  switch (status) {
    case "RESERVATION":
      return RERSERVATION_STATUS.OK;
    case "PRE_RESERVATION":
      return RERSERVATION_STATUS.PEND;
    case "CANCEL":
      return RERSERVATION_STATUS.CXL;
    default:
      return RERSERVATION_STATUS.KO;
  }
}

function getChargeStatus(status: string) {
  switch (status) {
    case "CANCELLED":
      return CHARGE_STATUS.CXL;
    case "PENDING":
    case "EXECUTION":
      return CHARGE_STATUS.PEND;
    case "OK":
      return RERSERVATION_STATUS.OK;
    default:
      return RERSERVATION_STATUS.KO;
  }
}

export function dtoToEntity(dto: AutomaticChargesDTO[]): AutomaticChargeEntity[] | [] {
  if (!dto?.length) {
    return [];
  }

  return dto.map<AutomaticChargeEntity>(charge => {
    let penaltyBrackets: PenaltyBracket[] | undefined;
    let penaltyRooms: number[] = [];
    const quantity = 1;
    const checkIn = new Date(charge.checkIn);
    const checkOut = new Date(charge.checkOut);
    const configurationBase = formatConfigurations(
      charge.cancellationPolicyConfigurationRaw.configurations
    );

    const configurations: CancellationPolicyConfigurationFromRoomStay[] = configurationBase.map(
      (it: CancellationPolicyConfigurationFromRoomStay) => {
        const maxReleaseDuration = it.maxReleaseDate
          ? differenceInDays(checkIn, it.maxReleaseDate)
          : undefined;
        const minReleaseDuration = it.minReleaseDate
          ? differenceInDays(checkIn, it.minReleaseDate)
          : undefined;
        const maxRelease: ReleaseConfiguration | undefined =
          maxReleaseDuration !== undefined
            ? {
                ...it.maxRelease,
                chronoUnit: "DAYS",
                duration: maxReleaseDuration,
              }
            : it.maxRelease;
        const minRelease: ReleaseConfiguration | undefined =
          minReleaseDuration !== undefined
            ? {
                ...it.minRelease,
                chronoUnit: "DAYS",
                duration: minReleaseDuration,
              }
            : it.minRelease;
        return {
          ...it,
          maxRelease,
          minRelease,
        };
      }
    );
    penaltyBrackets = buildPenaltyBrackets({
      configurations,
      checkIn,
      checkOut,
      cutOffDate: new Date(charge.roomStayDateCreation as string),
      currency: charge.currency,
      firstNightCost: charge.firstNightCost,
      totalRate: charge.totalRate,
    });

    penaltyRooms = penaltyBrackets.map(it => (it.type === "CHARGE_FIRST_NIGHT" ? 1 : quantity));

    const cancellationPolicy: CancellationPolicyEntity = {
      conditionTicker: charge.cancellationPolicyConfigurationRaw.conditionTicker,
      penaltyBrackets,
      penaltyRooms,
      rooms: quantity ?? 1,
    };

    return {
      id: charge.id,
      bookingId: charge.roomStayId,
      multipleBookingId: charge.reservationId,
      checkIn,
      checkOut,
      cancellationPolicy,
      paymentType: charge.paymentType,
      estimatedCollectDate: charge.estimatedCollectDate,
      effectiveCollectDate: charge.effectiveCollectDate,
      jobLastExecution: charge.jobLastExecution,
      chargeStatus: getChargeStatus(charge.chargeStatus),
      intents: charge.intents.toString(),
      amount: charge.amount,
      currency: charge.currency,
      totalRate: charge.totalRate,
      firstNightCost: charge.firstNightCost,
      status: getReservationStatus(charge.roomStayStatus),
      chargeAlert: charge.chargeAlert,
    };
  });
}
